import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Form, Table, Modal } from "react-bootstrap";
import appConstants from "../constants/appConstants";
import Layout from "../components/Layout";
import { elements } from "chart.js";

const getAuthToken = () => localStorage.getItem("authToken");

const AccountStatement = () => {
    const [accountId, setAccountId] = useState("");
    const [accountName, setAccountName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [statement, setStatement] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [openingBalance, setOpeningBalance] = useState(0);

    useEffect(() => {
        const fetchAccounts = async () => {
            const token = getAuthToken();
            if (!token) return;
            try {
                const response = await axios.get(`${appConstants.API_ROOT_URL}get-accounts`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAccounts(response.data.data);
            } catch (error) {
                console.error("Error fetching accounts", error);
            }
        };
        fetchAccounts();
        setDateRange('today');
    }, []);

    const handleAccountChange = (e) => {
        const input = e.target.value;
        setAccountName(input);
        
        const matches = accounts.filter(acc => acc.name.toLowerCase().includes(input.toLowerCase()));
        setFilteredAccounts(matches);
    };

    const selectAccount = (account) => {
        setAccountId(account.id);
        setAccountName(account.name);
        setFilteredAccounts([]);
    };

    const fetchStatement = async () => {
        if (!accountId) return alert("Please select a valid account");
        const token = getAuthToken();
        if (!token) return alert("Unauthorized! Please log in.");

        setLoading(true);
        try {
            const response = await axios.post(`${appConstants.API_ROOT_URL}get-account-statement`, {
                account_id: accountId,
                start_date: startDate,
                end_date: endDate
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if(response.data?.status == "success"){
                setStatement(response.data.transactions);
                setOpeningBalance(response.data.opening_balance);
                setShowModal(false);
            }
            else{
                alert(response.data?.message);
            }
        } catch (error) {
            alert("Error fetching account statement");
            console.error(error);
        }
        setLoading(false);
    };

    
    const setDateRange = (range) => {
        const today = new Date();
        let start, end;
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
    
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so we add 1
            const day = String(date.getDate()).padStart(2, '0'); // Ensure 2-digit day format
            return `${year}-${month}-${day}`;
        };
        
    
        switch (range) {
            case 'today':
                start = end = formatDate(today);
                break;
            case 'thisMonth':
                start = formatDate(new Date(currentYear, currentMonth, 1));
                end = formatDate(new Date(currentYear, currentMonth + 1, 0));
                break;
            case 'lastYear':
                const shift = -1;
                if (currentMonth >= 3) {
                    // On or after April 1, financial year starts this year
                    start = formatDate(new Date(currentYear + shift, 3, 1)); // April 1, current year
                    end = formatDate(new Date(currentYear + 1 + shift, 2, 31)); // March 31, next year
                } else {
                    // Before April 1, financial year started last year
                    start = formatDate(new Date(currentYear - 1 + shift, 3, 1)); // April 1, last year
                    end = formatDate(new Date(currentYear + shift, 2, 31)); // March 31, current year
                }
                break;
            case 'thisFinancialYear':
                if (currentMonth >= 3) {
                    // On or after April 1, financial year starts this year
                    start = formatDate(new Date(currentYear, 3, 1)); // April 1, current year
                    end = formatDate(new Date(currentYear + 1, 2, 31)); // March 31, next year
                } else {
                    // Before April 1, financial year started last year
                    start = formatDate(new Date(currentYear - 1, 3, 1)); // April 1, last year
                    end = formatDate(new Date(currentYear, 2, 31)); // March 31, current year
                }
                break;
            default:
                return;
        }
    
        setStartDate(start);
        setEndDate(end);
    };
    
    
    
        
    
    
    return (
        <Layout>
            <div className="p-3 bg-light">
                <h5>Account Statement</h5>
                <Button variant="primary" onClick={() => setShowModal(true)}>Get Statement</Button>
                


                <Modal show={showModal} onHide={() => setShowModal(false)}>
    <Modal.Header closeButton>
        <Modal.Title>Filter Account Statement</Modal.Title>
    </Modal.Header>
    <Modal.Body>
        <Form>
            <Form.Group className="mb-2" style={{ position: 'relative' }}>
                <Form.Label>Account</Form.Label>
                <Form.Control type="text" value={accountName} 
                    onChange={handleAccountChange} 
                    placeholder="Type account name..." />
                {filteredAccounts.length > 0 && (
                    <div className="suggestions-box">
                        {filteredAccounts.map((acc) => (
                            <div key={acc.id} className="suggestion-item" 
                                onClick={() => selectAccount(acc)}>
                                {acc.name}
                            </div>
                        ))}
                    </div>
                )}
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>End Date</Form.Label>
                <Form.Control type="date" value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)} />
            </Form.Group>
            <div className="d-flex justify-content-between mt-3">
                <Button variant="outline-primary" onClick={() => setDateRange('today')}>Today</Button>
                <Button variant="outline-primary" onClick={() => setDateRange('thisMonth')}>This Month</Button>
                <Button variant="outline-primary" onClick={() => setDateRange('lastYear')}>Last Year</Button>
                <Button variant="outline-primary" onClick={() => setDateRange('thisFinancialYear')}>This Financial Year</Button>
            </div>
        </Form>
    </Modal.Body>
    <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
        <Button variant="primary" onClick={fetchStatement} disabled={loading}>
            {loading ? "Fetching..." : "Fetch Statement"}
        </Button>
    </Modal.Footer>
</Modal>
                
                {statement.length > 0 && (
                    <>
                        <h6 className="mt-3">Account: {accountName}</h6>
                        <Table striped bordered hover className="mt-3">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>No</th>
                                    <th>Particulars</th>
                                    <th>Debit</th>
                                    <th>Credit</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                            <tr key={0}>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td>Opening Balance</td>
                                        <td></td>
                                        <td></td>
                                        <td>{Number(openingBalance).toFixed(2)}</td> {/* Ensure formatting */}
                                    </tr>
                                {statement.reduce((acc, entry, index) => {
                                    const prevBalance = index === 0 ? openingBalance : acc[index - 1].runningTotal;
                                    const change = entry.debit_credit === "D" ? parseFloat(entry.amount) : -parseFloat(entry.amount);
                                    const runningTotal = (prevBalance + change) || 0; // Ensure runningTotal is always a number

                                    const row = {
                                        ...entry,
                                        runningTotal: runningTotal, // Keep as a number
                                    };
                                    acc.push(row);

                                    return acc;
                                }, []).map((entry, index) => (
                                    <tr key={index+1}>
                                        <td>{entry.date}</td>
                                        <td>{entry.entry_type}</td>
                                        <td>{entry.transaction_number}</td>
                                        <td>{entry.opposite_account}</td>
                                        <td>{entry.debit_credit === "D" ? entry.amount : "-"}</td>
                                        <td>{entry.debit_credit === "C" ? entry.amount : "-"}</td>
                                        <td>{Number(entry.runningTotal).toFixed(2)}</td> {/* Ensure formatting */}
                                    </tr>
                                ))}
                            </tbody>

                        </Table>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default AccountStatement;
